/**
 * Synthèse vocale des missions chauffeur — entièrement sur l'appareil.
 *
 * Le texte est prononcé par le moteur vocal du téléphone, via l'API Web Speech. Aucun serveur,
 * aucun appel réseau, aucun compte tiers : la voix fonctionne en mode avion, ce qui compte pour
 * un chauffeur au port.
 *
 * Historique, parce qu'il explique la forme actuelle : la lecture passait d'abord par le backend,
 * qui relayait translate.google.com — point d'entrée non documenté de Google Traduction, utilisé
 * hors de ses conditions, sans compte ni voix choisie. Ce chemin a été supprimé plutôt que
 * remplacé : la voix de l'appareil, une fois essayée, s'est révélée suffisante, et la supprimer
 * retire du code au lieu d'en ajouter. Le contrôleur backend, sa règle de sécurité et son test
 * ont disparu avec lui.
 *
 * Contrepartie assumée : un téléphone sans voix française installée ne peut plus rien lire.
 * {@link speak} le signale par sa valeur de retour pour que l'écran le dise, au lieu de laisser
 * un bouton qui paraît ne rien faire.
 */

/**
 * Convertit les mots en MAJUSCULES en Title Case pour que la voix les lise comme des mots
 * normaux et non comme des acronymes.
 * Ex : « FASO EBENE » → « Faso Ebene », « DEPOT VRIDI » → « Depot Vridi »
 */
function toTitleCase(text: string): string {
    if (!text) return text;
    // Articles et prépositions français restent en minuscules
    const lower = new Set(['de','du','des','la','le','les','en','au','aux','et','à','un','une']);
    return text.toLowerCase().replace(/\b\w+/g, (w, i) =>
        (i > 0 && lower.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1)
    );
}

/**
 * Formate un numéro de conteneur lettre par lettre avec pause.
 * « MRKU6502946 » → « M R K U, 6 5 0 2 9 4 6 »
 */
function formatConteneur(code: string): string {
    const letters = code.replace(/\d.*/, '').split('').join(' ');
    const numbers = code.replace(/^[A-Z]+/, '').split('').join(' ');
    return `${letters}, ${numbers}`;
}

/** Prétraite le texte : conteneurs lettre par lettre, tout le reste en Title Case. */
function preprocess(text: string): string {
    // 1. Protéger les numéros de conteneurs avec un token
    const tokens: string[] = [];
    let result = text.replace(/\b([A-Z]{2,4}\d{5,})\b/g, (m) => {
        const idx = tokens.length;
        tokens.push(formatConteneur(m));
        return `§TOKEN${idx}§`;
    });

    // 2. Convertir TOUT le reste (MAJUSCULES) en Title Case
    result = result.replace(/\b[A-Z]{2,}(?:\s+[A-Z]{2,})*\b/g, toTitleCase);

    // 3. Abréviations portuaires
    result = result
        .replace(/\bTc\b/gi, 'Té Cé')
        .replace(/\bBl\b/gi, 'Bé El');

    // 4. Réinjecter les numéros de conteneurs
    result = result.replace(/§TOKEN(\d+)§/g, (_, i) => tokens[parseInt(i)]);

    return result;
}

/**
 * Choisit la voix française à employer, ou undefined si l'appareil n'en a aucune.
 *
 * Le filtre « féminine » ne retient que des marqueurs positifs. Il se terminait auparavant par
 * {@code includes('thomas') === false}, combiné par « ou » : il était donc vrai pour toute voix
 * ne s'appelant pas Thomas, si bien qu'une voix masculine nommée autrement passait pour
 * féminine et que les quatre premiers niveaux de priorité perdaient leur sens.
 */
function choisirVoix(): SpeechSynthesisVoice | undefined {
    const voix = window.speechSynthesis.getVoices();

    const estFeminine = (v: SpeechSynthesisVoice) => {
        const n = v.name.toLowerCase();
        return n.includes('female') || n.includes('femme')
            || n.includes('amélie') || n.includes('amelie')
            || n.includes('audrey') || n.includes('marie')
            || n.includes('julie')  || n.includes('virginie');
    };

    const priorites = [
        (v: SpeechSynthesisVoice) => v.lang === 'fr-FR' && estFeminine(v) && v.name.toLowerCase().includes('neural'),
        (v: SpeechSynthesisVoice) => v.lang === 'fr-FR' && estFeminine(v) && v.name.toLowerCase().includes('premium'),
        (v: SpeechSynthesisVoice) => v.lang === 'fr-FR' && estFeminine(v) && v.name.toLowerCase().includes('enhanced'),
        (v: SpeechSynthesisVoice) => v.lang === 'fr-FR' && estFeminine(v) && v.name.toLowerCase().includes('google'),
        (v: SpeechSynthesisVoice) => v.lang === 'fr-FR' && estFeminine(v),
        (v: SpeechSynthesisVoice) => v.lang === 'fr-FR' && !v.name.toLowerCase().includes('compact'),
        (v: SpeechSynthesisVoice) => v.lang.startsWith('fr'),
    ];

    for (const test of priorites) {
        const trouvee = voix.find(test);
        if (trouvee) return trouvee;
    }
    return undefined;
}

/** Message affiché quand l'appareil ne peut rien prononcer. */
export const MESSAGE_VOIX_ABSENTE =
    "Aucune voix française n'est installée sur ce téléphone. "
    + 'Paramètres → Synthèse vocale → Google → installer les données françaises.';

/** Vrai si l'appareil dispose d'une voix française utilisable. */
export function voixDisponible(): boolean {
    return 'speechSynthesis' in window && choisirVoix() !== undefined;
}

/**
 * Prononce le texte.
 *
 * @returns false si l'appareil n'a aucune voix française — l'écran doit alors afficher
 *          {@link MESSAGE_VOIX_ABSENTE} plutôt que de laisser croire à un bouton inerte.
 *          Sans voix française, le moteur lirait le texte avec la prononciation de sa langue
 *          par défaut : incompréhensible, et pire que le silence.
 */
export function speak(text: string): boolean {
    if (!('speechSynthesis' in window)) return false;

    const voix = choisirVoix();
    if (!voix) return false;

    window.speechSynthesis.cancel();

    const utt = new SpeechSynthesisUtterance(preprocess(text));
    utt.voice  = voix;
    utt.lang   = 'fr-FR';
    utt.rate   = 0.82;
    utt.pitch  = 1.05;
    utt.volume = 1.0;

    window.speechSynthesis.speak(utt);
    return true;
}

/** Stoppe la lecture en cours. */
export function stopSpeaking(): void {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
}

/** Construit le texte de mission à lire. */
export function buildMissionText(params: {
    prenom: string;
    conteneur: string;
    client: string;
    lieu: string;
    type: 'livraison' | 'recuperation';
}): string {
    const { prenom, conteneur, client, lieu, type } = params;
    // Les noms et lieux en MAJUSCULES seront convertis par preprocess()
    // Le conteneur sera épelé lettre par lettre par preprocess()
    if (type === 'livraison') {
        // « à » et non « au » : le lieu est un nom de commune ou de quartier — « à Marcory »,
        // « à Yopougon ». « au Marcory » sonnait faux à l'écoute, et c'est écouté que ce texte
        // existe.
        return `Bonjour ${prenom}. Vous devez livrer le conteneur ${conteneur}, chez ${client}, à ${lieu}.`;
    }
    return `Bonjour ${prenom}. Vous devez récupérer le conteneur ${conteneur}, chez ${client}, à ${lieu}.`;
}

export function speakSuccess(prenom: string): void {
    speak(`Bravo ${prenom}! Mission terminée avec succès. Excellent travail!`);
}
